import { dedent } from 'ts-dedent'
import { DataWarehouseConnection } from '..'
import { Workflow } from '../..'
import {
  ColumnDataType,
  NumericType,
  Provider,
  TableColumnDefinition,
  TableOptimizationInfo,
  WorkflowCodeType,
  WorkflowComponentParamType,
  WorkflowEnvVars,
  WorkflowTableSchemas,
  WorkflowVariable
} from '../../../types'
import { WorkflowComponent } from '../../../components/common/workflowcomponent'

export class OracleConnection extends DataWarehouseConnection {
  EXPIRATION_TIME_DAY = 30

  constructor(name: string, tempLocation: string, workspaceLocation?: string, extensionsLocation?: string) {
    // For Oracle, we only use tempLocation for now
    super(name, tempLocation, workspaceLocation || tempLocation, extensionsLocation || tempLocation, Provider.Oracle)
  }

  castValue(value: string, type: string) {
    return `CAST(${value} AS ${type})`
  }

  getTempSelectQuery(select: string, dryRun: boolean): string {
    if (dryRun) {
      return dedent`SELECT * FROM
                  (
                    ${select}
                  ) WHERE 1=0`
    } else {
      return select
    }
  }

  removeDatabaseName(tablename: string) {
    const parts = tablename.split('.')
    return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`
  }

  getCreateTableQuery(
    tablename: string,
    select: string,
    codeType: WorkflowCodeType,
    _optimizeBy: TableOptimizationInfo | null,
    _addOptimizationStats?: boolean
  ): string {
    select = dedent(select)
    const dryRun = codeType === WorkflowCodeType.DryRun

    const sql = dedent`
        BEGIN
          EXECUTE IMMEDIATE q'[
              CREATE TABLE ${this.quoteName(tablename)} AS
              ${this.getTempSelectQuery(select, dryRun)}
          ]';
        EXCEPTION
          WHEN OTHERS THEN
              IF SQLCODE = -955 THEN
                NULL; -- Table already exists, do nothing
              ELSE
                RAISE; -- Raise other unexpected errors
              END IF;
        END;`
    return sql
  }

  createTableFromSchema(schema: Array<TableColumnDefinition>, tableName: string): string {
    const columns = schema
      .map((col) => {
        let type = col.db_type || this.columnTypeToDataProviderType(col.type)
        if (type.toLowerCase() === 'varchar2') {
          type = 'VARCHAR2(4000)'
        }
        return `"${col.name}" ${type}`
      })
      .join(', ')
    const query = dedent`
      BEGIN
        EXECUTE IMMEDIATE q'[
            CREATE TABLE ${this.quoteName(tableName)} (${columns})
        ]';
      EXCEPTION
        WHEN OTHERS THEN
            IF SQLCODE = -955 THEN
              NULL; -- Table already exists, do nothing
            ELSE
              RAISE; -- Raise other unexpected errors
            END IF;
      END;
      `
    return this.wrapCodeInIfTableExistsBlock(this.wrapSQLCodeAs(query), [tableName], WorkflowCodeType.Full)
  }

  getCreateViewQuery(viewname: string, select: string, codeType: WorkflowCodeType): string {
    return this.getCreateTableQuery(viewname, select, codeType, null)
  }

  getCreateTempTableQuery(
    tablename: string,
    select: string,
    codeType: WorkflowCodeType,
    _optimizeBy: TableOptimizationInfo | null,
    _addOptimizationStats?: boolean
  ): string {
    return this.getCreateTableQuery(tablename, select, codeType, null)
  }

  getCreateTempViewQuery(viewname: string, select: string, codeType: WorkflowCodeType): string {
    return this.getCreateTableQuery(viewname, select, codeType, null)
  }

  getOutputTableName(
    workflowId: string,
    componentName: string,
    params: Record<string, unknown>,
    output: string,
    codeType: WorkflowCodeType
  ): string {
    const name = super.getOutputTableName(workflowId, componentName, params, output, codeType)

    // For stored procedures, parent returns just table name (1 part)
    // For others, parent returns tempLocation.tablename (2 parts)
    // Oracle needs schema.tablename format
    if (codeType === WorkflowCodeType.StoredProcedure) {
      // For stored procedures, prepend the temp schema
      return `${this.tempLocation}.${name.toUpperCase()}`
    } else {
      const parts = name.split('.')
      const tablename = parts.pop() as string
      return `${parts.join('.')}.${tablename.toUpperCase()}`
    }
  }

  applyCapitalization(tablename: string): string {
    return tablename.toUpperCase()
  }

  wrapComponentCodeInFlowControlBlock(query: string, codeType: WorkflowCodeType): string {
    return query
  }

  getStoredProcedureOutputTableNameExpression(_workflow: Workflow, _full: boolean): string {
    return ''
  }

  getDropTableQuery(tablename: string): string {
    return dedent`
      BEGIN
        EXECUTE IMMEDIATE 'DROP TABLE ${this.quoteName(tablename)}';
      EXCEPTION
        WHEN OTHERS THEN
            IF SQLCODE = -942 THEN
              NULL; -- Table doesn't exist, continue
            ELSE
              RAISE; -- Raise other unexpected errors
            END IF;
      END;`
  }

  wrapCodeInIfTableExistsBlock(
    query: string,
    _outputTables: Array<string>,
    _codeType: WorkflowCodeType,
    _outputTablesExpressions?: Array<string>,
    _variablePrefix?: string
  ): string {
    return query
  }

  async getWorkflowCode(
    workflow: Workflow,
    envVars: WorkflowEnvVars,
    codeType: WorkflowCodeType,
    schemas?: WorkflowTableSchemas
  ): Promise<string> {
    const queries = await workflow.getSubqueries(this, envVars, codeType, schemas)
    const executable = queries.some((q) => q.query)
    let query = ''
    if (executable) {
      if (codeType !== WorkflowCodeType.StoredProcedure) {
        // Use _getScriptBody to properly handle conditional components like ifelse
        query = await this._getScriptBody(workflow, envVars, queries, codeType, schemas)
        query = dedent`
                /*
                ${workflow.workflowMetadataAsCommentString(codeType)}
                */
               BEGIN
                ${query}
               END;`
        query = this.replaceVariablesInScriptQuery(query, workflow.variables)
      } else {
        return this._getProcedureBody(workflow, envVars, queries, schemas)
      }
    }

    return query
  }

  replaceVariablesInStoredProcedureQuery(
    _variables: Array<WorkflowVariable>,
    query: string,
    _requiresDynamicSQL: boolean
  ) {
    return query
  }

  paramTypeToDataProviderType(inputType: WorkflowComponentParamType) {
    switch (inputType) {
      case WorkflowComponentParamType.String:
        return 'CLOB'
      case WorkflowComponentParamType.Number:
        return 'BINARY_FLOAT'
      case WorkflowComponentParamType.Boolean:
        return 'NUMBER(1)'
      default:
        return 'CLOB'
    }
  }

  columnTypeToDataProviderType(colType: ColumnDataType) {
    switch (colType) {
      case ColumnDataType.String:
        return 'CLOB'
      case ColumnDataType.Number:
        return 'NUMBER'
      case ColumnDataType.Boolean:
        return 'NUMBER(1)'
      case ColumnDataType.Binary:
        return 'BLOB'
      case ColumnDataType.Date:
        return 'DATE'
      case ColumnDataType.Time:
        return 'TIME'
      case ColumnDataType.Datetime:
        return 'DATETIME'
      case ColumnDataType.Timestamp:
        return 'TIMESTAMP'
      case ColumnDataType.Geometry:
        return 'SDO_GEOMETRY'
      default:
        return 'CLOB'
    }
  }

  wrapCodeInProcedure(
    _name: string,
    _workflow: Workflow,
    body: string,
    _includeDiagram: boolean,
    _isImmutable: boolean
  ): string {
    return body
  }

  getCheckStoredProcedureExistsQueries(_workflow: Workflow, _isImmutable: boolean) {
    return []
  }

  getRemoveStoredProcedureQueries(_name: string): Array<string> {
    return []
  }

  extractErrorLineNumberFromApiResponse(_response: string): number | null {
    return null
  }

  getSourceTablePermissionsQuery(_workflow: Workflow) {
    return ''
  }

  getTempStorageSetupQueries(_options?: { location?: string }) {
    return ['SELECT \'{"success": true}\' AS result FROM DUAL']
  }

  getStoredProcedureCacheInvalidationQueries(_name: string): Array<string> {
    return []
  }

  getTableListQuery(filter: string): string {
    // Extract schema name from tempLocation (format: default.SCHEMA or just SCHEMA)
    let schemaName: string = this.tempLocation || 'ADMIN'

    // Handle the 'default.SCHEMA' format
    if (schemaName.includes('.')) {
      schemaName = schemaName.split('.').pop() || 'ADMIN'
    }

    // Ensure schema name is uppercase for Oracle
    schemaName = schemaName.toUpperCase()

    return dedent`
      SELECT table_name
      FROM all_tables
      WHERE owner = '${schemaName}'
      AND table_name LIKE '${filter.toUpperCase()}'`
  }

  dataProviderTypeToColumnType(type: string): ColumnDataType {
    switch (type) {
      case 'NUMBER':
      case 'BINARY_FLOAT':
      case 'BINARY_DOUBLE':
      case 'FLOAT':
      case 'DOUBLE':
      case 'INT':
      case 'INTEGER':
      case 'SMALLINT':
      case 'TINYINT':
      case 'BIGINT':
        return ColumnDataType.Number
      case 'DATE':
        return ColumnDataType.Date
      case 'TIME':
        return ColumnDataType.Time
      case 'TIMESTAMP':
        return ColumnDataType.Timestamp
      case 'BLOB':
        return ColumnDataType.Binary
      case 'SDO_GEOMETRY':
        return ColumnDataType.Geometry
      default:
        return ColumnDataType.String
    }
  }

  numericTypeToDataProviderType(numericType: NumericType) {
    return numericType === NumericType.Float ? 'NUMBER' : 'NUMBER(10)'
  }

  quoteName(name: string) {
    if (name === undefined || name === null) {
      return name
    }
    // Handle empty environment variable case - replace empty parts with ADMIN
    name = name.replace(/\.\./, '.ADMIN.')
    name = name.replace(/^\./, 'ADMIN.')
    name = name.replace(/\.$/, '.ADMIN')

    // Fix specific Oracle double schema issue: ADMIN.ADMIN.table -> ADMIN.table
    name = name.replace(/ADMIN\.ADMIN\./g, 'ADMIN.')

    // Convert any 3-part table names to 2-part names for Oracle
    // Oracle uses SCHEMA.TABLE format, not DATABASE.SCHEMA.TABLE
    const parts = name.split('.')
    if (parts.length === 3) {
      // Take the last two parts (schema.table)
      name = `${parts[1]}.${parts[2]}`
    }
    return name
  }

  quoteMultilineString(str: string) {
    return dedent`q'[${str}]'`
  }

  quoteVariableName(name: string) {
    return name
  }

  getExtensionUninstallQuery(_name: string): string {
    return ''
  }

  getStoredProceduresQuery(): string | null {
    return null
  }

  prepareSchemaQueries(queries: Array<string>): Array<string> {
    const returnQueries: Array<string> = []
    const last = queries.pop() as string
    returnQueries.push(dedent`
        BEGIN
          ${queries.join('\n')}
        END;
        ;
      `)
    returnQueries.push(last)
    return returnQueries
  }
  getOutputSchemasQueries(tableNames: Array<string>): string {
    const tempStoragePath = this.tempLocation
    const quotedTableNames = tableNames.map((name) => `'${name.toUpperCase()}'`)
    const quotedSchemaName = `'${tempStoragePath.split('.').pop() as string}'`.toUpperCase()

    // TODO: Handle ORA-01489 error gracefully when LISTAGG output exceeds MAX_STRING_SIZE
    // Currently using ON OVERFLOW ERROR to fail cleanly, but we might want to handle
    // this more gracefully in the future by chunking columns or using alternative approaches
    const query = dedent`
    WITH cols AS (
      SELECT
        table_name,
        column_name AS name,
        data_type AS db_type,
        CASE
          WHEN data_type IN ('NUMBER', 'FLOAT', 'BINARY_FLOAT', 'BINARY_DOUBLE') THEN '${ColumnDataType.Number}'
          WHEN data_type = 'CHAR' THEN '${ColumnDataType.String}'
          WHEN data_type LIKE 'VARCHAR%' THEN '${ColumnDataType.String}'
          WHEN data_type = 'DATE' THEN '${ColumnDataType.Date}'
          WHEN data_type = 'TIMESTAMP' THEN '${ColumnDataType.Timestamp}'
          WHEN data_type = 'RAW' THEN '${ColumnDataType.Binary}'
          WHEN data_type = 'CLOB' THEN '${ColumnDataType.String}'
          WHEN data_type = 'BLOB' THEN '${ColumnDataType.Binary}'
          WHEN data_type = 'SDO_GEOMETRY' THEN '${ColumnDataType.Geometry}'
          ELSE '${ColumnDataType.String}'
        END AS type,
        column_id AS ordinal_position
      FROM all_tab_columns
      WHERE owner = ${quotedSchemaName}
        AND table_name IN (${quotedTableNames.join(',')})
    )
    SELECT
      ${quotedSchemaName} || '.' || table_name AS fqn,
      '[' ||
        LISTAGG(
          '{"name":"' || name || '","type":"' || type || '","db_type":"' || db_type || '"}',
          ',' ON OVERFLOW ERROR
        ) WITHIN GROUP (ORDER BY ordinal_position)
      || ']' AS schema
    FROM cols
    GROUP BY table_name`

    return query
  }

  /**
   * Oracle doesn't require schema creation like other databases
   * since we use the ADMIN schema which already exists
   */
  getStorageSetupQueries(_options?: { location?: string }): Array<string> {
    // For Oracle, we'll return a simple validation query
    // to ensure we have access to the ADMIN schema
    return [
      dedent`
        -- Validate access to ADMIN schema
        SELECT 'Oracle connection validated' AS status FROM dual
      `
    ]
  }

  /**
   * Adapts workflow components for Oracle provider
   * - Filters out ColumnDataType.Geography (Oracle uses Geometry)
   * - Keeps only inputs that support Oracle provider
   * - Whitelists spatial conversion components that work with Oracle
   */
  adaptComponent(component: WorkflowComponent) {
    const whiteList = ['native.geographytogeometry']
    if (!whiteList.includes(component.metadata.name)) {
      for (const input of component.inputs) {
        if (input.type === WorkflowComponentParamType.Column && input.dataType) {
          input.dataType = (input.dataType as Array<ColumnDataType>).filter((x) => x !== ColumnDataType.Geography)
        }
      }
    }
    component.inputs = component.inputs.filter((x) => x.providers == null || x.providers.includes(Provider.Oracle))
  }

  /**
   * Returns the geometry data type used by Oracle for spatial columns
   * Oracle uses SDO_GEOMETRY, which corresponds to ColumnDataType.Geometry
   */
  getGeoDataType(): ColumnDataType {
    return ColumnDataType.Geometry
  }
}
